// @ts-check
// Filtros e ordenações em rascunho por view (doc 06, 2.4): ficam em memória até "Salvar"
// (PATCH /rest/metadata/views/:id) ou "Redefinir". A lista aberta por um clique num gráfico (contrato 07, seção 10)
// guarda o parâmetro `drilldown` no rascunho: ele restringe a lista, o groupBy, a planilha e a seleção, e nunca vai para a
// view salva.
import { signal } from '@preact/signals';
import { replaceViewLocally, fieldsById, objectsById } from '../../data/metadata-store.js';
import { navigate } from '../../app/router.js';
import { buildViewUrlParams, hasViewUrlParams, parseViewUrlParams, replaceViewUrlParams, stripDrilldownUrlParams } from './view-url-params.js';
import { readDrilldownParam } from '../../lib/drilldown-links.js';
import { updateView } from '../../data/rest.js';
import { buildFilterTree } from '../../lib/view-filters.js';
import { getOrderByForField } from '../../lib/field-metadata.js';
import { serializeOrderBy, serializeRestFilter } from '../../lib/rest-filter.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { generateId } from '../../lib/utils.js';
import { confirmLiveViewSave } from '../audiences/view-usage-confirm.js';
import { addGroup, addRule, ensureRoot, removeGroup, removeRule, setGroupOperator } from '../../lib/filter-tree-state.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/metadata-store.js').View} View
 * @typedef {import('../../lib/view-filters.js').ViewFilter} ViewFilter
 * @typedef {{ id: string, fieldMetadataId: string, direction: string, subFieldName?: string | null }} ViewSort
 * @typedef {{ id: string, parentViewFilterGroupId?: string | null, logicalOperator: string, positionInViewFilterGroup?: number | null }} ViewFilterGroup
 * @typedef {{ filters: import('@preact/signals').Signal<ViewFilter[]>, sorts: import('@preact/signals').Signal<ViewSort[]>,
 *   filterGroups: import('@preact/signals').Signal<ViewFilterGroup[]>,
 *   openFilterId: import('@preact/signals').Signal<string | null>, isAdvancedFilterOpen: import('@preact/signals').Signal<boolean>,
 *   isDeletedFilterActive: import('@preact/signals').Signal<boolean>, anyFieldSearch: import('@preact/signals').Signal<string>,
 *   isAnyFieldSearchOpen: import('@preact/signals').Signal<boolean>, drilldown: import('@preact/signals').Signal<string | null> }} ViewDraft
 */

/** @type {Map<string, ViewDraft>} */
const viewDrafts = new Map();

/**
 * @param {View} view
 * @returns {ViewDraft}
 */
export function getViewDraft(view) {
  let draft = viewDrafts.get(view.id);
  if (!draft) {
    draft = {
      filters: signal(cloneFilters(view.viewFilters)),
      sorts: signal(cloneSorts(view.viewSorts)),
      filterGroups: signal(cloneFilterGroups(view.viewFilterGroups)),
      openFilterId: signal(null),
      isAdvancedFilterOpen: signal(false),
      // lixeira (doc 06, chip "Deleted"): não é salvo na view, como no Twenty
      isDeletedFilterActive: signal(false),
      // busca em qualquer campo (anyFieldFilterValue do Twenty): gravada na view junto com os filtros
      anyFieldSearch: signal(view.anyFieldFilterValue ?? ''),
      isAnyFieldSearchOpen: signal(false),
      // `f.<receita>` ou `s.<receita>` da lista aberta por um clique num gráfico
      drilldown: signal(/** @type {string | null} */ (null)),
    };
    viewDrafts.set(view.id, draft);
  }
  return draft;
}

/**
 * @param {ViewFilter[]} filters
 */
function cloneFilters(filters) {
  return (filters ?? []).map((filter) => ({ ...filter }));
}

/**
 * @param {ViewFilterGroup[] | undefined} groups
 */
function cloneFilterGroups(groups) {
  return (groups ?? []).map((group) => ({ ...group }));
}

/**
 * @param {ViewFilterGroup[]} groups
 */
function comparableFilterGroups(groups) {
  return JSON.stringify(groups.map(({ id, parentViewFilterGroupId, logicalOperator }) => ({ id, parentViewFilterGroupId: parentViewFilterGroupId ?? null, logicalOperator })));
}

/**
 * @param {ViewSort[]} sorts
 */
function cloneSorts(sorts) {
  return (sorts ?? []).map((sort) => ({ ...sort }));
}

/**
 * @param {ViewFilter[]} filters
 */
function comparableFilters(filters) {
  return JSON.stringify(
    filters.map(({ fieldMetadataId, operand, value, subFieldName, viewFilterGroupId }) => ({
      fieldMetadataId, operand, value: value ?? '', subFieldName: subFieldName ?? null, viewFilterGroupId: viewFilterGroupId ?? null,
    })),
  );
}

/**
 * @param {ViewSort[]} sorts
 */
function comparableSorts(sorts) {
  return JSON.stringify(sorts.map(({ fieldMetadataId, direction, subFieldName }) => ({ fieldMetadataId, direction: String(direction).toLowerCase(), subFieldName: subFieldName ?? null })));
}

/**
 * @param {View} view
 */
export function isViewDraftDirty(view) {
  const draft = getViewDraft(view);
  return (
    comparableFilters(draft.filters.value) !== comparableFilters(view.viewFilters) ||
    comparableSorts(draft.sorts.value) !== comparableSorts(view.viewSorts) ||
    comparableFilterGroups(draft.filterGroups.value) !== comparableFilterGroups(view.viewFilterGroups ?? []) ||
    draft.anyFieldSearch.value.trim() !== (view.anyFieldFilterValue ?? '').trim()
  );
}

/**
 * Os filtros, os grupos ou a busca do rascunho diferem dos salvos (as ordenações não mudam o público)?
 * @param {View} view
 */
function areViewFiltersDirty(view) {
  const draft = getViewDraft(view);
  return comparableFilters(draft.filters.value) !== comparableFilters(view.viewFilters)
    || comparableFilterGroups(draft.filterGroups.value) !== comparableFilterGroups(view.viewFilterGroups ?? [])
    || draft.anyFieldSearch.value.trim() !== (view.anyFieldFilterValue ?? '').trim();
}

/**
 * Rascunho igual à view salva, sem mexer na URL.
 * @param {View} view
 */
function restoreSavedDraft(view) {
  const draft = getViewDraft(view);
  draft.filters.value = cloneFilters(view.viewFilters);
  draft.sorts.value = cloneSorts(view.viewSorts);
  draft.filterGroups.value = cloneFilterGroups(view.viewFilterGroups);
  draft.isDeletedFilterActive.value = false;
  draft.anyFieldSearch.value = view.anyFieldFilterValue ?? '';
  draft.drilldown.value = null;
}

/**
 * Volta para a view como está salva; na lista aberta por um clique num gráfico, sai também do recorte do painel.
 * @param {View} view
 */
export function resetViewDraft(view) {
  const hadDrilldown = getViewDraft(view).drilldown.value !== null;
  restoreSavedDraft(view);
  if (hadDrilldown) removeDrilldownFromUrl(view);
  syncViewUrlWithDraft(view);
}

/** Parâmetro `drilldown` ativo na view (lista aberta por um clique num gráfico), ou null. @param {View} view */
export const getViewDrilldown = (view) => getViewDraft(view).drilldown.value;

/** "Limpar" do chip do painel: a view volta a ser a salva. @param {View} view */
export const clearViewDrilldown = (view) => resetViewDraft(view);

// Lixeira (docs/arquitetura-appcrm/08-contrato-lixeira.md): a view mostra só os registros excluídos

/** Filtro REST que liga a lixeira no motor (`deletedAt` mencionado no filtro). */
export const DELETED_RECORDS_FILTER = Object.freeze({ field: 'deletedAt', operator: 'is', value: 'NOT_NULL' });

/** @param {View} view */
export const isDeletedFilterActive = (view) => getViewDraft(view).isDeletedFilterActive.value;

/** @param {View} view @param {boolean} isActive */
export function setDeletedFilterActive(view, isActive) {
  getViewDraft(view).isDeletedFilterActive.value = isActive;
}

/**
 * Página de índice aberta agora (objeto e view), para o menu de comandos oferecer "Ver excluídos".
 * @type {import('@preact/signals').Signal<{ objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata, view: View } | null>}
 */
export const activeRecordIndexState = signal(null);

/**
 * Filtros, grupos e ordenações do rascunho no formato da API (salvar ou criar uma view a partir da atual).
 * @param {View} view
 */
export function getViewDraftPayload(view) {
  const draft = getViewDraft(view);
  const groupIds = new Set(draft.filterGroups.value.map((group) => group.id));
  return {
    viewFilters: draft.filters.value.map(({ id, fieldMetadataId, operand, value, subFieldName, displayValue, viewFilterGroupId, positionInViewFilterGroup }) => ({
      id, fieldMetadataId, operand, value, subFieldName: subFieldName ?? null, displayValue: displayValue ?? null,
      viewFilterGroupId: viewFilterGroupId && groupIds.has(viewFilterGroupId) ? viewFilterGroupId : null,
      positionInViewFilterGroup: positionInViewFilterGroup ?? null,
    })),
    viewFilterGroups: draft.filterGroups.value.map(({ id, parentViewFilterGroupId, logicalOperator, positionInViewFilterGroup }) => ({
      id, parentViewFilterGroupId: parentViewFilterGroupId ?? null, logicalOperator, positionInViewFilterGroup: positionInViewFilterGroup ?? null,
    })),
    viewSorts: draft.sorts.value.map(({ id, fieldMetadataId, direction, subFieldName }) => ({
      id, fieldMetadataId, direction: String(direction).toLowerCase(), subFieldName: subFieldName ?? null,
    })),
    anyFieldFilterValue: draft.anyFieldSearch.value.trim() || null,
  };
}

/**
 * @param {View} view
 */
export async function saveViewDraft(view) {
  const { viewFilters, viewFilterGroups, viewSorts, anyFieldFilterValue } = getViewDraftPayload(view);
  // view da escola usada ao vivo por campanha ou fluxo: mudar os filtros muda esse público agora (contrato 09 de público)
  if (areViewFiltersDirty(view) && !(await confirmLiveViewSave(view, objectsById.value.get(view.objectMetadataId)))) return;
  try {
    const updated = await updateView(view.id, { viewFilters, viewFilterGroups, viewSorts, anyFieldFilterValue });
    const saved = { ...view, ...(updated && typeof updated === 'object' ? updated : {}), viewFilters, viewFilterGroups, viewSorts, anyFieldFilterValue };
    replaceViewLocally(saved);
    // a view agora guarda o que o link trazia: a URL volta a ser só a da view
    syncViewUrlWithDraft(saved);
    showSuccessSnackbar(t('viewBar.viewSaved'));
  } catch (error) {
    showErrorSnackbar(error);
  }
}

/**
 * @param {View} view
 * @param {string} fieldMetadataId
 * @param {string} operand
 * @param {string | null} [subFieldName] sub-campo de campo composto (sobrenome, cidade, valor)
 */
export function addDraftFilter(view, fieldMetadataId, operand, subFieldName = null) {
  const draft = getViewDraft(view);
  const filter = { id: generateId(), fieldMetadataId, operand, value: '', subFieldName };
  draft.filters.value = [...draft.filters.value, filter];
  draft.openFilterId.value = filter.id;
  return filter;
}

/**
 * @param {View} view
 * @param {string} filterId
 * @param {Partial<ViewFilter>} changes
 */
export function updateDraftFilter(view, filterId, changes) {
  const draft = getViewDraft(view);
  draft.filters.value = draft.filters.value.map((filter) => (filter.id === filterId ? { ...filter, ...changes } : filter));
}

/**
 * @param {View} view
 * @param {string} filterId
 */
export function removeDraftFilter(view, filterId) {
  const draft = getViewDraft(view);
  draft.filters.value = draft.filters.value.filter((filter) => filter.id !== filterId);
  if (draft.openFilterId.value === filterId) draft.openFilterId.value = null;
}

/**
 * Um campo só tem uma ordenação; clicar de novo alterna a direção (doc 06, 5.2 e 12).
 * @param {View} view
 * @param {string} fieldMetadataId
 * @param {'asc' | 'desc' | null} [direction]
 * @param {string | null} [subFieldName] sub-campo de campo composto; ausente mantém o atual
 */
export function setDraftSort(view, fieldMetadataId, direction = null, subFieldName = undefined) {
  const draft = getViewDraft(view);
  const existing = draft.sorts.value.find((sort) => sort.fieldMetadataId === fieldMetadataId);
  if (existing) {
    const nextDirection = direction ?? (String(existing.direction).toLowerCase() === 'asc' ? 'desc' : 'asc');
    draft.sorts.value = draft.sorts.value.map((sort) => (sort.fieldMetadataId === fieldMetadataId
      ? { ...sort, direction: nextDirection, ...(subFieldName !== undefined ? { subFieldName } : {}) } : sort));
  } else {
    draft.sorts.value = [...draft.sorts.value, { id: generateId(), fieldMetadataId, direction: direction ?? 'asc', subFieldName: subFieldName ?? null }];
  }
}

/**
 * Busca em qualquer campo do rascunho (chip "Qualquer campo" da barra).
 * @param {View} view @param {string} text
 */
export function setDraftAnyFieldSearch(view, text) {
  getViewDraft(view).anyFieldSearch.value = text.slice(0, 100);
}

/**
 * @param {View} view
 * @param {string} sortId
 */
export function removeDraftSort(view, sortId) {
  const draft = getViewDraft(view);
  draft.sorts.value = draft.sorts.value.filter((sort) => sort.id !== sortId);
}

/**
 * Filtro e ordenação REST da view (rascunho), mais um filtro extra opcional (coluna do kanban).
 * @param {View} view
 */
export function getViewQuery(view) {
  const draft = getViewDraft(view);
  const getField = (/** @type {string} */ id) => fieldsById.value.get(id);
  const viewFilter = buildFilterTree(draft.filters.value, getField, draft.filterGroups.value);
  const search = draft.anyFieldSearch.value.trim();
  const searchFilter = search ? { field: 'anyField', operator: 'search', value: search } : null;
  const parts = /** @type {import('../../lib/rest-filter.js').FilterNode[]} */ ([viewFilter, searchFilter, draft.isDeletedFilterActive.value ? DELETED_RECORDS_FILTER : null].filter(Boolean));
  const filter = parts.length === 0 ? null : parts.length === 1 ? parts[0] : { and: parts };
  const orderBy = draft.sorts.value.flatMap((sort) => {
    const field = getField(sort.fieldMetadataId);
    return field ? getOrderByForField(field, String(sort.direction).toUpperCase(), sort.subFieldName ?? null) : [];
  });
  // Sem ordenação explícita, a ordem manual (position) manda, como no Twenty.
  if (orderBy.length === 0) orderBy.push({ field: 'position', direction: 'AscNullsFirst' });
  // lista aberta por um clique num gráfico: toda consulta da lista leva o recorte (s. restringe aos registros do ponto)
  const drilldown = draft.drilldown.value;
  return { filter, orderBy, drilldown, key: `${serializeRestFilter(filter)}|${serializeOrderBy(orderBy)}|${drilldown ?? ''}` };
}

// Filtro avançado (doc 06, seção 11): um grupo raiz com regras e subgrupos de um nível, cada grupo com E ou OU. As
// regras são as puras de lib/filter-tree-state.js, as mesmas do construtor do público

/** @param {View} view @returns {import('../../lib/filter-tree-state.js').FilterTreeState} */
function draftTreeState(view) {
  const draft = getViewDraft(view);
  return { filters: draft.filters.value, filterGroups: draft.filterGroups.value };
}

/** @param {View} view @param {import('../../lib/filter-tree-state.js').FilterTreeState} state */
function writeDraftTreeState(view, state) {
  const draft = getViewDraft(view);
  if (draft.filters.value !== state.filters) draft.filters.value = state.filters;
  if (draft.filterGroups.value !== state.filterGroups) draft.filterGroups.value = state.filterGroups;
  if (draft.filterGroups.value.length === 0) draft.isAdvancedFilterOpen.value = false;
}

/**
 * Grupo raiz do filtro avançado, criado com uma regra quando ainda não existe.
 * @param {View} view @param {import('../../lib/field-metadata.js').FieldMetadata} firstField @param {string} operand
 */
export function ensureAdvancedFilterRoot(view, firstField, operand) {
  const { state, root } = ensureRoot(draftTreeState(view), firstField.id, operand, generateId);
  writeDraftTreeState(view, state);
  return root;
}

/**
 * @param {View} view @param {string} groupId @param {string} fieldMetadataId @param {string} operand
 */
export function addAdvancedRule(view, groupId, fieldMetadataId, operand) {
  const { state, filter } = addRule(draftTreeState(view), groupId, fieldMetadataId, operand, generateId);
  writeDraftTreeState(view, state);
  return filter;
}

/**
 * Subgrupo do raiz, que nasce com E e uma regra.
 * @param {View} view @param {string} rootId @param {string} fieldMetadataId @param {string} operand
 */
export function addAdvancedGroup(view, rootId, fieldMetadataId, operand) {
  writeDraftTreeState(view, addGroup(draftTreeState(view), rootId, fieldMetadataId, operand, generateId));
}

/**
 * @param {View} view @param {string} groupId @param {'AND' | 'OR'} logicalOperator
 */
export function setAdvancedGroupOperator(view, groupId, logicalOperator) {
  writeDraftTreeState(view, setGroupOperator(draftTreeState(view), groupId, logicalOperator));
}

/**
 * Remove o grupo, os subgrupos e as regras deles; o raiz vazio também sai.
 * @param {View} view @param {string} groupId
 */
export function removeAdvancedGroup(view, groupId) {
  writeDraftTreeState(view, removeGroup(draftTreeState(view), groupId));
}

/**
 * Remove uma regra avançada e os grupos que ficarem vazios.
 * @param {View} view @param {string} filterId
 */
export function removeAdvancedRule(view, filterId) {
  writeDraftTreeState(view, removeRule(draftTreeState(view), filterId));
}

// Filtros e ordenações na URL (doc 06, seção 14): o link abre a lista já filtrada, como rascunho que só vai para a
// view quando alguém salva. A URL acompanha o rascunho, então copiar o endereço também compartilha a lista.

/** Última busca da URL sincronizada por view, para não reaplicar o que a própria tela escreveu. @type {Map<string, string>} */
const syncedUrlByViewId = new Map();

/**
 * Aplica no rascunho os filtros da URL quando ela traz parâmetros da view que a tela ainda não aplicou. A chegada de
 * um clique num gráfico (`drilldown`) troca o rascunho inteiro, mesmo sem `filter[...]`: os filtros salvos da view
 * (de equipe, que qualquer um pode ter mudado) não podem cortar o conjunto do painel.
 * @param {View} view @param {string} search busca atual da URL
 */
export function applyViewUrlParams(view, search) {
  const searchParams = new URLSearchParams(search);
  const drilldown = readDrilldownParam(searchParams);
  const draft = getViewDraft(view);
  // saiu da lista do painel (menu lateral, outro link): a view volta a ser a salva
  if (!drilldown && draft.drilldown.value !== null) {
    restoreSavedDraft(view);
    syncedUrlByViewId.delete(view.id);
  }
  if ((!hasViewUrlParams(searchParams) && !drilldown) || syncedUrlByViewId.get(view.id) === search) return;
  syncedUrlByViewId.set(view.id, search);
  const objectMetadata = objectsById.value.get(view.objectMetadataId);
  const parsed = parseViewUrlParams(searchParams, objectMetadata?.fields ?? [], generateId)
    ?? (drilldown ? { filters: [], filterGroups: [], sorts: [], anyFieldSearch: '' } : null);
  if (!parsed) return;
  draft.filters.value = parsed.filters;
  draft.filterGroups.value = parsed.filterGroups;
  draft.sorts.value = parsed.sorts;
  draft.anyFieldSearch.value = parsed.anyFieldSearch;
  if (drilldown) {
    draft.drilldown.value = drilldown;
    draft.isDeletedFilterActive.value = false;
  }
}

/**
 * Tira da URL o recorte do painel (drilldown e os parâmetros da view), sem empilhar histórico.
 * @param {View} view
 */
function removeDrilldownFromUrl(view) {
  if (typeof window === 'undefined') return;
  const location = window.location;
  const viewIdParam = new URLSearchParams(location.search).get('viewId');
  if (viewIdParam !== null && viewIdParam !== view.id) return;
  const search = stripDrilldownUrlParams(location.search);
  syncedUrlByViewId.set(view.id, search);
  if (search !== location.search) navigate(location.pathname + search + location.hash, { replace: true });
}

/**
 * Parâmetros da URL que reproduzem o rascunho da view (vazio quando ela está igual à salva).
 * @param {View} view
 * @returns {Array<[string, string]>}
 */
export function getViewDraftUrlPairs(view) {
  if (!isViewDraftDirty(view)) return [];
  const draft = getViewDraft(view);
  return buildViewUrlParams(
    { filters: draft.filters.value, filterGroups: draft.filterGroups.value, sorts: draft.sorts.value, anyFieldSearch: draft.anyFieldSearch.value },
    (fieldMetadataId) => fieldsById.value.get(fieldMetadataId),
  );
}

/**
 * Escreve na URL (sem empilhar histórico) o rascunho da view aberta.
 * @param {View} view
 */
export function syncViewUrlWithDraft(view) {
  if (typeof window === 'undefined') return;
  const location = window.location;
  const viewIdParam = new URLSearchParams(location.search).get('viewId');
  // só a view aberta escreve na URL
  if (viewIdParam !== null && viewIdParam !== view.id) return;
  const search = replaceViewUrlParams(location.search, getViewDraftUrlPairs(view));
  syncedUrlByViewId.set(view.id, search);
  if (search !== location.search) navigate(location.pathname + search + location.hash, { replace: true });
}

/**
 * Link da view com o rascunho atual (filtros, ordenações e busca), para "Copiar link".
 * @param {View} view @param {string} objectNamePlural
 */
export function buildViewShareUrl(view, objectNamePlural) {
  const params = new URLSearchParams();
  if (!view.id.startsWith('fallback-')) params.set('viewId', view.id);
  for (const [key, value] of getViewDraftUrlPairs(view)) params.append(key, value);
  const text = params.toString();
  return window.location.origin + '/objects/' + objectNamePlural + (text ? '?' + text : '');
}
